import type { OpenAPI } from "simplydo/interfaces"

type Impact = OpenAPI.GET<"/organisations/{id}/impacts">["response"]["impacts"][number]

import { Divider, Icon } from "semantic-ui-react"

export const impactMultipliers = {
  "one-off": 1,
  annually: 1,
  monthly: 12,
  weekly: 52,
}
type Frequency = keyof typeof impactMultipliers

const forceNumber = (value: string, multiplier?: number) => {
  if (!value) return
  const number = Number(value)
  if (Number.isNaN(number)) return
  return number * (multiplier ?? 1)
}

const formatNumber = (value: string, multiplier?: number) => {
  const number = forceNumber(value, multiplier)
  if (!number) return value

  if (window.Intl) {
    return new Intl.NumberFormat().format(number)
  }
  return `${number}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
}

export const formatRange = (
  impact: Impact,
  from: string,
  to?: string,
  options?: {
    frequency?: Frequency
  },
) => {
  if (!impact || from === undefined || from === null || from === "") {
    return ""
  }

  let fromValue: string | number = from
  let toValue: string | number | undefined = to

  let prefix = ""
  let suffix = ""

  const multiplier = options?.frequency ? (impactMultipliers[options.frequency] ?? 1) : 1

  if (impact.type === "time") {
    fromValue = forceNumber(fromValue, multiplier) ?? fromValue
    if (toValue) {
      toValue = forceNumber(toValue, multiplier) ?? toValue
    }
    suffix = " hours"
  } else {
    fromValue = formatNumber(fromValue, multiplier)
    if (toValue) {
      toValue = formatNumber(toValue, multiplier)
    }
  }

  if (impact.type === "money") {
    prefix = "£"
  }
  return `${prefix}${fromValue}${toValue ? ` - ${toValue}` : ""}${suffix}`
}

export const formatImpact = (
  orgImpact: Impact,
  impact: any,
  options?: {
    annually?: boolean
  },
) => {
  return formatRange(orgImpact, impact.choice?.value ?? impact.value, impact.choice?.toRange, {
    frequency: options?.annually ? (impact.frequency ?? "annually") : undefined,
  })
}

export const formatOption = (
  impact: Impact,
  option: {
    value: string
    toRange?: string
  },
) => {
  return formatRange(impact, option.value, option.toRange)
}

export const annualImpactValue = (impact, usesImpactStatementSystem) => {
  if (usesImpactStatementSystem) {
    return formatImpact(impact.ownerImpact, impact, {
      annually: true,
    })
  }

  const value = impact.choice?.value ?? impact.value
  if (!value) {
    return ""
  }

  if (typeof value === "string") {
    return value
  }
  const annual = (impactMultipliers[impact?.frequency] ?? 1) * value
  if (impact?.ownerImpact?.type === "money") {
    return `£${annual}`
  }
  return annual
}

export const generateImpactText = (impact, usesImpactStatementSystem, t) => {
  const effectMap = {
    cost: "cost",
    saving: "save",
  }

  const effectText = effectMap[impact?.ownerImpact?.effect] ?? `have ${t("generic.impactWithArticle")} of`
  let text = `This ${t("generic.idea")} will ${effectText} `
  if (usesImpactStatementSystem) {
    text += formatImpact(impact.ownerImpact, impact)
  } else {
    if (impact?.ownerImpact?.type === "time") {
      text += `${impact.choice?.value ?? impact.value} hours`
    } else if (impact?.ownerImpact?.type === "money") {
      text += `£${impact.choice?.value ?? impact.value}`
    } else {
      text += `"${impact.choice?.value ?? impact.value}"`
    }
  }

  if (!impact?.frequency || impact?.frequency === "one-off") {
    text += " once"
  } else {
    text += ` ${impact?.frequency}`
    if (impact?.frequency !== "annually") {
      text += ` (${annualImpactValue(impact, usesImpactStatementSystem)} annually)`
    }
  }
  text += "."

  return (
    <>
      <p>
        <strong>{impact?.ownerImpact?.name}</strong>
        {impact?.ownerImpactCategory ? (
          <span>
            <br />
            in {impact?.ownerImpactCategory?.name}
          </span>
        ) : null}
      </p>
      <p>{text}</p>
      {impact.choice?.weight !== undefined ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            lineHeight: "1em",
          }}
        >
          <Icon name="level up alternate" size="small" style={{ marginRight: 2 }} />
          <div>
            Weight: {impact.choice?.weight || 0}/{impact.maxWeight || 0}
          </div>
        </div>
      ) : null}
      {impact?.justification ? (
        <>
          <Divider />
          <b>Justification</b>
          <p>{impact?.justification}</p>
        </>
      ) : null}
    </>
  )
}
